import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, getDocs, doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { Table, Modal, Form, Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import * as XLSX from 'xlsx'; // Import xlsx for Excel download
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import styles from './AdminOrders.module.css';
const LOGO_URL = '/logo.jpg';

const MySwal = withReactContent(Swal);

// --- image helpers for light thumbnails ---
// Build a -w{width}.webp URL beside the original (keeps the ?token intact)
const toWebpWidth = (url, width = 1200) => {
  if (!url || typeof url !== 'string') return '';
  const [base, qs] = url.split('?');
  return base.replace(/(\.[^.?#/]+)$/, `-w${width}.webp`) + (qs ? `?${qs}` : '');
};

// Fallback: if WebP 404s, switch to the original only once (avoid loops)
// Fallback chain: WEBP -> original -> logo
const fallbackToOriginalThenLogo = (e, originalUrl) => {
  const img = e.currentTarget;
  if (!img) return;
  const step = img.dataset.fallbackStep || '0'; // 0: webp, 1: original, 2: logo
  if (step === '0') {
    if (originalUrl) {
      img.dataset.fallbackStep = '1';
      img.src = originalUrl;
    } else {
      img.dataset.fallbackStep = '2';
      img.src = LOGO_URL;
    }
  } else if (step === '1') {
    img.dataset.fallbackStep = '2';
    img.src = LOGO_URL;
  }
};


// (Optional) HTTP function to backfill/generate missing WebP variants.
// Replace <REGION> and <PROJECT_ID> with yours if you deployed the helper.
const BACKFILL_URL =
  'https://<REGION>-<PROJECT_ID>.cloudfunctions.net/backfillMissingWebpForPrefix';

const triggerProductsBackfill = async () => {
  try {
    const res = await fetch(`${BACKFILL_URL}?prefix=products/&width=1200`);
    const json = await res.json();
    Swal.fire(
      'Backfill WebP',
      `Generados: ${json.made || 0}\nYa existían: ${json.already || 0}`,
      'info'
    );
  } catch (err) {
    Swal.fire('Backfill WebP', 'No se pudo ejecutar el backfill.', 'error');
  }
};


const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [newPaymentStatus, setNewPaymentStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const firestore = getFirestore();
  const [showImageModal, setShowImageModal] = useState(false); // State to manage image modal visibility
  const [loadingImages, setLoadingImages] = useState({}); // Track image loading states
  const [selectedStatusFilter, setSelectedStatusFilter] = useState(''); // For the dropdown filter
  const [filteredOrders, setFilteredOrders] = useState([]); // For displaying filtered data
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState('');
  const [highlightedOrderId, setHighlightedOrderId] = useState(null);


  const [selectedImage, setSelectedImage] = useState(null);

  const handleImageLoad = (id) => {
    setLoadingImages((prev) => ({ ...prev, [id]: true }));
  };
  

const handleImageClick = (imageUrl) => {
  setSelectedImage(imageUrl || LOGO_URL);
  setShowImageModal(true);
};

  const handleCloseImageModal = () => {
    setSelectedImage(null); // Clear the image URL
    setShowImageModal(false); // Hide the modal
  };

  const [selectedPaymentStatusFilter, setSelectedPaymentStatusFilter] = useState(''); // New state for Payment Status filter
  const [weeklyOrders, setWeeklyOrders] = useState([]);
  const [weekDates, setWeekDates] = useState([]);
  useEffect(() => {
    // Function to generate a rolling 7-day window
    const generateWeek = () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Normalize today's date
  
      const days = ["L", "K", "M", "J", "V", "S", "D"];
      const newWeekDates = [];
  
      for (let i = 0; i < 7; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i); // Move forward 7 days from today
  
        const dayIndex = (date.getDay() + 6) % 7; // Adjust to match ["L", "K", "M", ...]
        
        newWeekDates.push({
          dayLetter: days[dayIndex],
          date: date.toISOString().split("T")[0], // Format YYYY-MM-DD
          displayDate: `${days[dayIndex]} ${date.getDate()} ${date.toLocaleString("es-CR", { month: "short" })}`,
        });
      }
  
      setWeekDates(newWeekDates);
    };
  
    generateWeek();
  }, []);
  
  
  useEffect(() => {
    // Subscribe to Firestore for real-time order updates
    const fetchOrdersRealtime = () => {
      const q = query(collection(firestore, "orders"));
      return onSnapshot(q, (snapshot) => {
        const ordersData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  
        // Count orders per delivery date
        const ordersByDate = {};
        ordersData.forEach(order => {
          if (order.deliveryDate) {
            ordersByDate[order.deliveryDate] = (ordersByDate[order.deliveryDate] || 0) + 1;
          }
        });
  
        setWeeklyOrders(ordersByDate);
      });
    };
  
    const unsubscribe = fetchOrdersRealtime();
    return () => unsubscribe(); // Cleanup on unmount
  }, [firestore]);

  const handlePrintOrderDetails = async () => {
    const modalContent = document.getElementById('orderDetailsModalContent');
    if (!modalContent) return;
  
    // PDF dimensions
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
  
    // Render the content using html2canvas
    const canvas = await html2canvas(modalContent, {
      scale: 2,
      useCORS: true, // For cross-origin images
    });
  
    const imgData = canvas.toDataURL('image/png');
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
  
    // Scale the content to fit the width of the PDF
    const scale = pdfWidth / imgWidth;
    const scaledHeight = imgHeight * scale;
  
    if (scaledHeight <= pdfHeight) {
      // Content fits within one page
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, scaledHeight);
    } else {
      // Content needs to span multiple pages
      let yPosition = 0;
  
      while (yPosition < imgHeight) {
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.min(canvas.height - yPosition, pdfHeight / scale);
  
        const pageContext = pageCanvas.getContext('2d');
        pageContext.drawImage(
          canvas,
          0,
          yPosition,
          canvas.width,
          pageCanvas.height,
          0,
          0,
          canvas.width,
          pageCanvas.height
        );
  
        const pageImgData = pageCanvas.toDataURL('image/png');
        const pageScaledHeight = (pageCanvas.height * pdfWidth) / canvas.width;
  
        if (yPosition === 0) {
          pdf.addImage(pageImgData, 'PNG', 0, 0, pdfWidth, pageScaledHeight);
        } else {
          pdf.addPage();
          pdf.addImage(pageImgData, 'PNG', 0, 0, pdfWidth, pageScaledHeight);
        }
  
        yPosition += pageCanvas.height;
      }
    }
  
    pdf.save('Detalles_Pedido.pdf');
  };
  
  
  const handlePaymentStatusFilterChange = (event) => {
    const selectedPaymentStatus = event.target.value;
    setSelectedPaymentStatusFilter(selectedPaymentStatus);
  
    let filtered = orders;
  
    if (selectedStatusFilter) {
      filtered = filtered.filter(order => order.status === selectedStatusFilter);
    }
    
    if (selectedDeliveryDate) {
      filtered = filtered.filter(order => order.deliveryDate === selectedDeliveryDate);
    }
  
    if (selectedPaymentStatus) {
      filtered = filtered.filter(order => order.paymentStatus === selectedPaymentStatus);
    }
  
    setFilteredOrders(filtered);
  };
  
  

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      const q = query(collection(firestore, 'orders'));
      const querySnapshot = await getDocs(q);
      const ordersData = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      const sortedOrders = ordersData.sort((a, b) => new Date(b.date) - new Date(a.date));
      setOrders(sortedOrders);
      setFilteredOrders(sortedOrders); // Initialize filteredOrders
      setLoading(false);
    };
  
    fetchOrders();
  }, [firestore]);
  
  const handleStatusFilterChange = (event) => {
    const selectedStatus = event.target.value;
    setSelectedStatusFilter(selectedStatus);
  
    if (selectedStatus === '') {
      setFilteredOrders(orders); // Show all orders when no filter is selected
    } else {
      setFilteredOrders(orders.filter((order) => order.status === selectedStatus));
    }
  };

  const shippingLabel = (method) => {
    switch (method) {
      case 'pickup':
        return 'Recoger en el Local';
      case 'mandarRecoger':
        return 'Mandar a Recoger (Uber, Uber Flash, etc.)';
      case 'delivery':
        return 'Envío a Domicilio';
      default:
        return method || 'No especificado';
    }
  };
  
  
  
  const handleDownloadReport = () => {
    const formattedOrders = orders.map((order) => {
      // Generate product details in the format "Product Name: URL"
      const productDetails = (order.items || [])
        .map((item) => `${item.name}: https://armonyfloristeria.com/products/${item.id}`)
        .join(', '); // Join multiple products with commas
  
      return {
        'Número de Orden': order.orderNumber || 'N/A',
        'Fecha de Pedido': new Date(order.date).toLocaleString('es-CR'),
        'Día de Entrega': order.deliveryDate
          ? order.deliveryDate.split('-').reverse().join('/')
          : 'N/A',
        'Hora de Entrega': order.deliveryRange || 'N/A',
        'Nombre Cliente': `${order.contactInfo?.recipientName} ${
          order.contactInfo?.recipientLastName || ''
        }`,
        'Teléfono': order.contactInfo?.telefono || 'N/A',
        'Email': order.email || 'N/A',
        'Productos': productDetails,
        'Método de Entrega': shippingLabel(order.shippingMethod),
        'Código Descuento': order.discount?.code || '',
        'Monto Descuento CRC': order.discount?.amount || 0,
        'Comisión PayPal CRC': order.paypalFeeCRC || 0,
        'Total': order.total,
        'Status Orden': order.status,
        'Status Pago': order.paymentStatus || 'Pago Pendiente',
      };

    });
  
    const worksheet = XLSX.utils.json_to_sheet(formattedOrders);
worksheet['!cols'] = [
  { wch: 20 }, // Número de Orden
  { wch: 20 }, // Fecha de Pedido
  { wch: 20 }, // Día de Entrega
  { wch: 20 }, // Hora de Entrega
  { wch: 30 }, // Nombre Cliente
  { wch: 15 }, // Teléfono
  { wch: 30 }, // Email
  { wch: 100 }, // Productos
  { wch: 25 }, // Método de Entrega
  { wch: 20 }, // Código Descuento
  { wch: 20 }, // Monto Descuento
  { wch: 20 }, // Comisión PayPal
  { wch: 15 }, // Total
  { wch: 20 }, // Status Orden
  { wch: 20 }, // Status Pago
];

  
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Pedidos');
  
    // Write file
    XLSX.writeFile(workbook, 'Reporte_Pedidos.xlsx');
  };
  

  const handleSaveOrderStatus = async () => {
    if (!newStatus) {
      MySwal.fire({
        title: 'Seleccione un estado válido',
        icon: 'warning',
        confirmButtonText: 'OK',
      });
      return;
    }

    setLoading(true);
    const orderDocRef = doc(firestore, 'orders', selectedOrder.id);

    try {
      await updateDoc(orderDocRef, { status: newStatus });

      // Update state immediately
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === selectedOrder.id
            ? { ...order, status: newStatus }
            : order
        )
      );

      setFilteredOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === selectedOrder.id
            ? { ...order, status: newStatus }
            : order
        )
      );

      // 🔆 Resaltar la fila actualizada por unos segundos
      setHighlightedOrderId(selectedOrder.id);
      setTimeout(() => {
        setHighlightedOrderId((current) =>
          current === selectedOrder.id ? null : current
        );
      }, 3000);

      MySwal.fire({
        title: 'Estado de la orden actualizado!',
        icon: 'success',
        confirmButtonText: 'OK',
      });
    } catch (error) {
      console.error('Error updating order status:', error);
      MySwal.fire({
        title: 'Error al actualizar estado',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    } finally {
      setShowStatusModal(false);
      setLoading(false);
    }
  };

  const handleSavePaymentStatus = async () => {
    if (!newPaymentStatus) {
      MySwal.fire({
        title: 'Seleccione un estado de pago válido',
        icon: 'warning',
        confirmButtonText: 'OK',
      });
      return;
    }

    setLoading(true);
    const orderDocRef = doc(firestore, 'orders', selectedOrder.id);

    try {
      await updateDoc(orderDocRef, { paymentStatus: newPaymentStatus });

      // Update state immediately
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === selectedOrder.id
            ? { ...order, paymentStatus: newPaymentStatus }
            : order
        )
      );

      setFilteredOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === selectedOrder.id
            ? { ...order, paymentStatus: newPaymentStatus }
            : order
        )
      );

      // 🔆 Resaltar la fila actualizada por unos segundos
      setHighlightedOrderId(selectedOrder.id);
      setTimeout(() => {
        setHighlightedOrderId((current) =>
          current === selectedOrder.id ? null : current
        );
      }, 3000);

      MySwal.fire({
        title: 'Estado de pago actualizado!',
        icon: 'success',
        confirmButtonText: 'OK',
      });
    } catch (error) {
      console.error('Error updating payment status:', error);
      MySwal.fire({
        title: 'Error al actualizar estado de pago',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    } finally {
      setShowPaymentModal(false);
      setLoading(false);
    }
  };

  const handleOpenStatusModal = (order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setShowStatusModal(true);
  };

  const handleOpenPaymentModal = (order) => {
    setSelectedOrder(order);
    setNewPaymentStatus(order.paymentStatus);
    setShowPaymentModal(true);
  };

  const handleOpenOrderDetailsModal = (order) => {
    setSelectedOrder(order);
    setShowOrderDetailsModal(true);
  };

  const handleCloseModals = () => {
    setShowStatusModal(false);
    setShowPaymentModal(false);
    setShowOrderDetailsModal(false);
    setSelectedOrder(null);
    setNewStatus('');
    setNewPaymentStatus('');
  };

    // Cierra solo el modal de "Ver Orden" y resalta la fila
  const handleCloseOrderDetailsModal = () => {
    if (selectedOrder) {
      setHighlightedOrderId(selectedOrder.id);

      setTimeout(() => {
        setHighlightedOrderId((current) =>
          current === selectedOrder.id ? null : current
        );
      }, 3000); // 3 segundos resaltado
    }

    setShowOrderDetailsModal(false);
    setSelectedOrder(null);
  };


  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const handleDeliveryDateFilterChange = (event) => {
    const selectedDate = event.target.value;
    setSelectedDeliveryDate(selectedDate);
  
    if (!selectedDate) {
      setFilteredOrders(orders); // Si no hay fecha, muestra todas las órdenes
    } else {
      setFilteredOrders(
        orders.filter((order) => order.deliveryDate === selectedDate)
      );
    }
  };
  
  return (

      <div className={styles.adminOrdersContainer}>
        {loading && <div className={styles.loadingOverlay}></div>}
    
        <h2 className={styles.adminOrdersTitle}>Manejo de Ordenes</h2>

        {/* Weekly Order Overview */}
<div className={styles.weeklyOrdersContainer}>
  <h3 className={styles.weeklyOrdersTitle}>Órdenes de la Semana</h3>
  <div className={styles.weeklyOrdersTable}>
    {weekDates.map(({ dayLetter, date, displayDate }) => (
      <div key={date} className={styles.weeklyOrdersCell}>
        <span className={styles.weeklyOrdersDate}>{displayDate}</span>
        <div className={styles.weeklyOrdersCount}>
          {weeklyOrders[date] || 0}
        </div>
      </div>
    ))}
  </div>
</div>

        {/* Filter Dropdown */}
        <div className={styles.filterContainer}>
          <label htmlFor="statusFilter" className={styles.filterLabel}>
            Filtrar por Status Orden:
          </label>
          <select
            id="statusFilter"
            className={styles.filterSelect}
            value={selectedStatusFilter}
            onChange={handleStatusFilterChange}
          >
            <option value="">Todos</option>
            <option value="Pendiente">Pendiente</option>
            <option value="Procesando">Procesando</option>
            <option value="En Espera de Pago">En Espera de Pago</option>
            <option value="Listo para Recolección">Listo para Recolección</option>
            <option value="En camino">En camino</option>
            <option value="Entregado">Entregado</option>
            <option value="Pedido Confirmado">Pedido Confirmado</option>
            <option value="Cancelado">Cancelado</option>
            <option value="Cancelado por el Vendedor">Cancelado por el Vendedor</option>
          </select>
        </div>

        <div className={styles.filterContainer}>
  <label htmlFor="paymentStatusFilter" className={styles.filterLabel}>
    Filtrar por Status Pago:
  </label>
  <select
    id="paymentStatusFilter"
    className={styles.filterSelect}
    value={selectedPaymentStatusFilter}
    onChange={handlePaymentStatusFilterChange}
  >
    <option value="">Todos</option>
    <option value="Pago Pendiente">Pago Pendiente</option>
    <option value="Pago Parcial">Pago Parcial</option>
    <option value="Pagado">Pagado</option>
    <option value="Pagado en el Local">Pagado en el Local</option>
    <option value="Cancelado">Cancelado</option>
  </select>
</div>

        <div className={styles.filterContainer}>
            <label htmlFor="deliveryDateFilter" className={styles.filterLabel}>
              Filtrar por Fecha de Entrega:
            </label>
            <input
              type="date"
              id="deliveryDateFilter"
              className={styles.filterInput}
              value={selectedDeliveryDate}
              onChange={handleDeliveryDateFilterChange}
            />
          </div>
    
        <Button onClick={handleDownloadReport} className={styles.adminOrdersButton}>
          Descargar Reporte
        </Button>

            {/* Scrollable wrapper so the table doesn't break the page on mobile */}
      <div className={styles.tableWrapper}>
        <Table
          striped
          bordered
          hover
          className={styles.adminOrdersTable}
        >
          <thead>
            <tr>
              <th>Número de Orden</th>              
              <th>Fecha Entrega</th>
              <th>Nombre Cliente</th>
              <th>Productos</th>
              <th>Total</th>
              <th>Status Orden</th>
              <th>Status Pago</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((order) => (
              <tr
                key={order.id}
                className={
                  highlightedOrderId === order.id ? styles.highlightedRow : ''
                }
              >
                <td>{order.orderNumber || 'N/A'}</td>
                <td>
                  {order.deliveryDate
                    ? `${order.deliveryDate
                        .split('-')
                        .reverse()
                        .join('/')} ${order.deliveryRange || ''}`
                    : 'Sin fecha de entrega'}
                </td>

                <td>
                  {order.contactInfo?.recipientName}{' '}
                  {order.contactInfo?.recipientLastName || ''}
                </td>

                <td>
                  {order.items.map((item, index) => (
                    <div key={index} className={styles.productInfo}>
                      <span>{item.name}</span>
                      <div className={styles.cardImgContainer}>
                        {!loadingImages[item.id] && (
                          <div
                            className={`${styles.imagePlaceholder} ${styles.spinner}`}
                          ></div>
                        )}
                        <img
                          // If there is no original URL at all, start with the logo
                          src={
                            item.principalImageUrl
                              ? toWebpWidth(item.principalImageUrl, 1200)
                              : LOGO_URL
                          }
                          alt={item.name || 'Producto'}
                          className={`${styles.productImage} ${
                            loadingImages[item.id] ? styles.imageLoaded : ''
                          }`}
                          loading="lazy"
                          onLoad={() => handleImageLoad(item.id)}
                          onError={(e) =>
                            fallbackToOriginalThenLogo(
                              e,
                              item.principalImageUrl
                            )
                          }
                          onClick={() =>
                            handleImageClick(
                              item.principalImageUrl || LOGO_URL
                            )
                          }
                        />
                      </div>
                    </div>
                  ))}
                </td>

                <td>
                <div>{formatCurrency(order.total)}</div>
                {order.discount && order.discount.amount > 0 && (
                  <div className={styles.discountTableTag}>
                    {order.discount.code} (-{formatCurrency(order.discount.amount || 0)})
                  </div>
                )}
              </td>

                <td>{order.status}</td>
                <td>{order.paymentStatus || 'Pago Pendiente'}</td>

                <td>
                  <button
                    className={styles.adminOrdersButton}
                    onClick={() => handleOpenStatusModal(order)}
                  >
                    Actualizar Status
                  </button>
                  <button
                    className={styles.adminOrdersButton}
                    onClick={() => handleOpenPaymentModal(order)}
                  >
                    Actualizar Status Pago
                  </button>
                  <button
                    className={styles.adminOrdersButton}
                    onClick={() => handleOpenOrderDetailsModal(order)}
                  >
                    Ver Orden
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

  
      {/* Modal for Enlarged Image */}
      <Modal show={showImageModal} onHide={handleCloseImageModal} centered>
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title>Imagen del Producto</Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          {selectedImage && (
              <img
                src={selectedImage}
                alt="Product"
                className={styles.fullImage}
                onError={(e) => { e.currentTarget.src = LOGO_URL; }}
              />
            )}

        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button onClick={handleCloseImageModal} className={styles.modalCloseButton}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
  
      {/* Modal for Order Status */}
      <Modal show={showStatusModal} onHide={handleCloseModals}>
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title>Actualizar Orden Status</Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <Form>
            <Form.Group controlId="status">
              <Form.Label>Status</Form.Label>
              <Form.Control
                as="select"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <option disabled value="">
                  Seleccionar Status
                </option>
                <option value="Pendiente">Pendiente</option>
                <option value="Procesando">Procesando</option>
                <option value="En Espera de Pago">En Espera de Pago</option>
                <option value="Listo para Recolección">Listo para Recolección</option>
                <option value="En camino">En camino</option>
                <option value="Entregado">Entregado</option>
                <option value="Pedido Confirmado">Pedido Confirmado</option>
                <option value="Cancelado">Cancelado</option>
                <option value="Cancelado por el Vendedor">Cancelado por el Vendedor</option>
              </Form.Control>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button onClick={handleSaveOrderStatus} className={styles.modalSaveButton}>
            Guardar
          </Button>
          <Button onClick={handleCloseModals} className={styles.modalCloseButton}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
  
      {/* Modal for Payment Status */}
      <Modal show={showPaymentModal} onHide={handleCloseModals}>
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title>Actualizar Status Pago</Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <Form>
            <Form.Group controlId="paymentStatus">
              <Form.Label>Status Pago</Form.Label>
              <Form.Control
                as="select"
                value={newPaymentStatus}
                onChange={(e) => setNewPaymentStatus(e.target.value)}
              >
                <option disabled value="">
                  Seleccionar Status Pago
                </option>
                <option value="Pago Pendiente">Pago Pendiente</option>
                <option value="Pago Parcial">Pago Parcial</option>
                <option value="Pagado">Pagado</option>

                <option value="Pagado en el Local">Pagado en el Local</option>
                <option value="Cancelado">Cancelado</option>
              </Form.Control>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button onClick={handleSavePaymentStatus} className={styles.modalSaveButton}>
            Guardar
          </Button>
          <Button onClick={handleCloseModals} className={styles.modalCloseButton}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
  
{/* Modal for Viewing Order Details */}
<Modal show={showOrderDetailsModal} onHide={handleCloseOrderDetailsModal} size="lg">
  <Modal.Header closeButton className={styles.modalHeader}>
    <Modal.Title>Detalles del Pedido</Modal.Title>
    <Button onClick={handlePrintOrderDetails} className={styles.printButton}>
      Descargar Pedido
    </Button>
  </Modal.Header>
  <Modal.Body className={styles.modalBody}>
    {/* Wrap the modal content in a div with an ID for printing */}
    <div id="orderDetailsModalContent">
      {selectedOrder && (
        <div>
          <h4>Información del Cliente</h4>
          <p>
            <strong>Nombre:</strong> {selectedOrder.contactInfo?.recipientName}{' '}
            {selectedOrder.contactInfo?.recipientLastName || ''}
          </p>
          <p>
            <strong>Teléfono:</strong> {selectedOrder.contactInfo?.telefono || 'N/A'}
          </p>
          <p>
            <strong>Email:</strong> {selectedOrder.email}
          </p>

          {/* "Quien Recibe" Section */}
          <h4>Quien Recibe</h4>
          <p>
            <strong>Nombre:</strong> {selectedOrder.quienRecibe?.name || 'N/A'}
          </p>
          <p>
            <strong>Teléfono:</strong> {selectedOrder.quienRecibe?.phone || 'N/A'}
          </p>

          <h4>Información de Envío</h4>
{selectedOrder.shippingMethod === 'delivery' && (
  <div>
    <p><strong>Dirección:</strong> {selectedOrder.shippingInfo?.direccion || 'N/A'}</p>
    <p><strong>Provincia:</strong> {selectedOrder.shippingInfo?.provincia || 'N/A'}</p>
    <p><strong>Cantón:</strong> {selectedOrder.shippingInfo?.canton || 'N/A'}</p>
    <p><strong>Distrito:</strong> {selectedOrder.shippingInfo?.distrito || 'N/A'}</p>
    <p><strong>Código Postal:</strong> {selectedOrder.shippingInfo?.codigoPostal || 'N/A'}</p>
  </div>
)}

{selectedOrder.shippingMethod === 'pickup' && (
  <p>
    <strong>Retiro en tienda:</strong> Plaza Vistana Oeste, Lindora, frente a Taco Bell, San José, Pozos.
  </p>
)}

{selectedOrder.shippingMethod === 'courier_pickup' && (
  <div>
    <p><strong>Entrega a repartidor del cliente (Uber, Uber Flash, mensajero, etc.)</strong></p>
    <p><strong>Punto de entrega:</strong> Plaza Vistana Oeste, Lindora, frente a Taco Bell, San José, Pozos.</p>
    <p>
      Prepararemos y embalaremos el pedido con el mayor cuidado y <strong>avisaremos cuando lo
      entreguemos al repartidor</strong> que el cliente indique.
    </p>
    <p>
      <strong>Importante:</strong> La garantía de transporte por daños durante el traslado
      aplica únicamente para <strong>“Envío (Armony)”</strong>. En “Mandar a recoger”, nuestra
      responsabilidad se limita hasta el momento de entrega al repartidor.
    </p>
  </div>
)}


          {/* Always show these fields */}
          <p>
            <strong>Fecha de Entrega:</strong> {selectedOrder.deliveryDate || 'N/A'}
          </p>
          <p>
            <strong>Horario de Entrega:</strong> {selectedOrder.deliveryRange || 'N/A'}
          </p>
          <p>
            <strong>Comentarios Adicionales:</strong> {selectedOrder.additionalComments || 'N/A'}
          </p>

          <h4>Información de Pago</h4>
          <p>
            <strong>Método de Pago:</strong> {selectedOrder.paymentMethod || 'N/A'}
          </p>
          {Number(selectedOrder.paypalFeeCRC) > 0 && (
            <p><strong>Comisión PayPal:</strong> {formatCurrency(selectedOrder.paypalFeeCRC)}</p>
          )}


          <h4>Resumen del Pedido</h4>
          <ul className={styles.orderSummary}>
            {selectedOrder.items.map((item, index) => (
              <li key={index} className={styles.orderItem}>
              <img
                src={item.principalImageUrl ? toWebpWidth(item.principalImageUrl, 1200) : LOGO_URL}
                alt={item.name || 'Producto'}
                className={styles.orderItemImage}
                onError={(e) => fallbackToOriginalThenLogo(e, item.principalImageUrl)}
                onClick={() => handleImageClick(item.principalImageUrl || LOGO_URL)}
              />


                <div>
                  <p>
                    <strong>{item.name}</strong>
                  </p>
                  <p>
                    {formatCurrency(item.price)} x {item.quantity}
                  </p>
                  {item.option && <p><strong>Opción:</strong> {item.option.name}</p>}
                  {item.tarjetaMessage && (
                    <p>
                      <strong>Mensaje en la Tarjeta:</strong> {item.tarjetaMessage}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>

                    {selectedOrder.discount && selectedOrder.discount.amount > 0 && (
            <>
              <p>
                <strong>
                  Descuento ({selectedOrder.discount.code}):
                </strong>{' '}
                -{formatCurrency(selectedOrder.discount.amount || 0)}
              </p>
              <p style={{ fontSize: '0.9rem', color: '#666' }}>
                Tipo:{' '}
                {selectedOrder.discount.type === 'percent'
                  ? `${selectedOrder.discount.value}%`
                  : formatCurrency(selectedOrder.discount.value || 0)}
              </p>
            </>
          )}

                    <p>
            <strong>Entrega:</strong>{' '}
            {formatCurrency(selectedOrder.deliveryPrice || 0)}
          </p>


          <p>
            <strong>Total:</strong> {formatCurrency(selectedOrder.total)}
          </p>

        </div>
      )}
    </div>
  </Modal.Body>
  <Modal.Footer className={styles.modalFooter}>
    <Button onClick={handleCloseOrderDetailsModal} className={styles.modalCloseButton}>
      Cerrar
    </Button>
  </Modal.Footer>
</Modal>



    </div>
  );
  
  
};

export default AdminOrders;
